import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Button } from '@mui/material';
import BlockData from '../../../models/BlockData';
import BlockProps from '../../../models/BlockProps';
import CarouselModal from '../../Modals/CarouselModal/CarouselModal';
import styles from './BlockCarouselWithPureJs.module.css';
import BlockCarouselData from './BlockCarouselData';
import CarouselItem from './components/CarouselItem';
import { BlockHeight } from '../../../models/BlockSettings';

export interface BlockCarouselWithPureJsProps extends BlockProps {
  blockData: BlockData[]
}

const DefaultAutoInterval = 6000;
function BlockCarouselWithPureJs({
  data, blockData, editHandler, isEditing, isTemplate,
}: BlockCarouselWithPureJsProps) {
  const props = (data.data as BlockCarouselData);
  const [openModal, setOpenModal] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const slidesRef = useRef<HTMLDivElement>(null);
  const leftNaviButton = useRef<HTMLButtonElement>(null);
  const rightNaviButton = useRef<HTMLButtonElement>(null);

  const first = props.carousel[0];
  const last = props.carousel[props.carousel.length - 1];

  const [height, setHeight] = useState('');

  useEffect(() => {
    if (props.height === BlockHeight.TALL) {
      setHeight('650px');
    } else {
      setHeight('300px');
    }
  }, [props.height]);

  const uiCarouselData = [{
    id: 'last-copy',
    image: last.image,
    link: last.link,
  }, ...props.carousel, {
    id: 'first-copy',
    image: first.image,
    link: first.link,
  }];

  const rightBoundary = -uiCarouselData.length * 100 + 100;

  const interval = useMemo(() => (props.autoInterval != null ? props.autoInterval * 1000 : DefaultAutoInterval), [props.autoInterval]);

  const isDragingRef = useRef<boolean>(false);
  const basePositionRef = useRef<number>(0);
  const baseTranslateX = useRef<number>(0);

  const getCurrentTranslate = useCallback(():number => {
    const translate = slidesRef.current.style.transform;
    const regex = /(-?[\d\\.]+)%/;
    const match = translate.match(regex);
    const currentTranslateX = parseInt(match[1], 10); // "-100%"
    return currentTranslateX;
  }, []);

  const handleRewind = useCallback((offset:number) => {
    if (offset === 0) {
      // reach the the left, jump to the last slide with the animation in 0s
      setTimeout(() => {
        slidesRef.current.style.transition = 'all 0s ease-in-out';
        slidesRef.current.style.transform = `translate(${-(uiCarouselData.length - 2) * 100}%)`;
      }, 600);
    }

    if (offset === rightBoundary) {
      // reach the right, jump to the first slide with the animation in 0s
      setTimeout(() => {
        slidesRef.current.style.transition = 'all 0s ease-in-out';
        slidesRef.current.style.transform = 'translate(-100%)';
      }, 600);
    }
  }, [rightBoundary, uiCarouselData.length]);

  const goToPrevious = (currentTranslateX:number) => {
    let previous = currentTranslateX;

    if (previous % 100 === 0) {
      previous += 100;
    } else {
      previous -= previous % 100;
    }

    handleRewind(previous);
    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
    slidesRef.current.style.transform = `translateX(${previous}%)`;
  };

  const goToNext = useCallback((currentTranslateX:number) => {
    let next = currentTranslateX;
    if (next % 100 === 0) {
      next -= 100;
    } else {
      next -= next % 100;
      next -= 100;
    }

    handleRewind(next);
    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
    slidesRef.current.style.transform = `translateX(${next}%)`;
  }, [handleRewind]);

  useEffect(() => {
    sliderRef.current.setAttribute('auto', `${props.auto}`);
    sliderRef.current.setAttribute('auto_interval', `${interval}`);
    if (props.auto === false) {
      return () => {};
    }

    const id = setInterval(() => {
      goToNext(getCurrentTranslate());
    }, interval);

    return () => {
      clearInterval(id);
    };
  }, [props.auto, interval, goToNext, getCurrentTranslate]);

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpenModal(true);
    }
  }, [isEditing, isTemplate]);

  const handleClose = () => setOpenModal(false);

  const getDirection = (baseClientX: number, clientX:number): 'left' | 'right' => {
    if (clientX < baseClientX) {
      return 'right';
    }
    return 'left';
  };

  const remainPrevious = (currentTranslateX:number) => {
    if (currentTranslateX % 100 === 0) {
      return;
    }

    const v = currentTranslateX - (currentTranslateX % 100);
    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
    slidesRef.current.style.transform = `translateX(${v}%)`;
  };

  const remainNext = (currentTranslateX:number) => {
    if (currentTranslateX % 100 === 0) {
      return;
    }

    const v = currentTranslateX - (currentTranslateX % 100) - 100;
    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
    slidesRef.current.style.transform = `translateX(${v}%)`;
  };

  const handleStart = (currentClientX: number) => {
    isDragingRef.current = true;
    basePositionRef.current = currentClientX;
    baseTranslateX.current = getCurrentTranslate();
  };

  const handleMove = (currentClientX: number) => {
    if (isDragingRef.current === false) {
      return;
    }
    const nextTranslateX = baseTranslateX.current + (100 * (currentClientX - basePositionRef.current)) / sliderRef.current.clientWidth;
    slidesRef.current.style.transform = `translateX(${nextTranslateX}%)`;
    slidesRef.current.style.transition = 'all 0s ease-in-out';
  };

  const handleEnd = (currentClientX: number) => {
    if (isDragingRef.current === false) {
      return;
    }
    isDragingRef.current = false;
    const currentTranslateX = getCurrentTranslate();

    if (getDirection(basePositionRef.current, currentClientX) === 'right') {
      if (currentTranslateX % 100 <= -20) {
        goToNext(currentTranslateX);
      } else {
        remainPrevious(currentTranslateX);
      }
    }

    if (getDirection(basePositionRef.current, currentClientX) === 'left') {
      if (currentTranslateX % 100 >= -80) {
        goToPrevious(currentTranslateX);
      } else {
        remainNext(currentTranslateX);
      }
    }

    slidesRef.current.style.transition = 'all 0.6s ease-in-out';
  };

  return (
    <>
      <div className="blockCarouselSliderRoot" style={{ position: 'relative' }}>
        <div
          ref={sliderRef}
          role="presentation"
          id="blockCarouselSlider"
          className={[styles.slider, 'blockCarouselSlider'].join(' ')}
          onTouchStart={(e) => {
            handleStart(e.touches[0].clientX);
          }}
          onTouchMove={(e) => {
            handleMove(e.touches[0].clientX);
          }}
          onTouchEnd={(e) => {
            handleEnd(e.changedTouches[0].clientX);
          }}
          onMouseDown={(e) => {
            handleStart(e.clientX);
          }}
          onMouseMove={(e) => {
            handleMove(e.clientX);
          }}
          onMouseUp={(e) => {
            handleEnd(e.clientX);
          }}
          onMouseLeave={(e) => {
            handleEnd(e.clientX);
          }}
        >
          <div id="blockCarouselSlides" ref={slidesRef} className={[styles.slides, 'blockCarouselSlides'].join(' ')} style={{ transform: 'translateX(-100%)' }}>
            {uiCarouselData.map((item) => (
              <div
                className={styles.slidesContainer}
                key={item.id}
                id={`slide-${item.id}`}
                style={{
                  maxHeight: '100%',
                  height: `${height}`,
                }}
              >
                <CarouselItem handleOpen={handleOpen} item={item} maxHeight={(props.height === BlockHeight.TALL ? 650 : 350)} />
              </div>
            ))}
          </div>
        </div>
        <Button ref={leftNaviButton} startIcon={<KeyboardArrowLeftIcon />} id="blockCarouselLeftNavi" type="button" onClick={() => goToPrevious(getCurrentTranslate())} className={[styles.leftNaviButton, 'blockCarouselLeftNavi'].join(' ')} />
        <Button ref={rightNaviButton} startIcon={<KeyboardArrowRightIcon />} id="blockCarouselRightNavi" type="button" onClick={() => goToNext(getCurrentTranslate())} className={[styles.rightNaviButton, 'blockCarouselRightNavi'].join(' ')} />

      </div>

      <CarouselModal
        open={openModal}
        carousel={props.carousel}
        slideId="slideId"
        auto={props.auto}
        autoInterval={props.autoInterval || 1}
        blockData={blockData}
        handleClose={handleClose}
        handleSave={(newItems, newAuto, autoInterval) => {
          props.auto = newAuto;
          props.autoInterval = autoInterval;
          props.carousel = newItems;
          editHandler(data.id, props);
          handleClose();
        }}
      />
    </>
  );
}

export default BlockCarouselWithPureJs;
